import { Location } from '@angular/common';
import { Component, HostListener, Inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TenantDataService } from 'app/config/services/tenant-data.service';
import { getTenantCurrencyConfig } from 'app/config/services/tenant-currency-config.util';
import { CurrencyCode } from 'app/library/course-offering/enums/currency-code.enum';
import { SidenavService } from 'app/library/grow/services/sidenav.service';
import { ExperienceWidgetService } from 'app/library/experience-widgets/services/experience-widget.service';
import { MaxBrainExperience } from 'app/library/experience/models/experience.model';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { ExperienceService } from 'app/library/experience/services/experience.service';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { SnackBarTime } from 'app/projects/core/src/lib/constants/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FilterLanguagesPipe } from 'app/projects/shared/src/lib/pipes/filter-languages.pipe';
import { IMaxBrainEnvironment } from 'app/projects/shared/src/lib/interfaces/environment.interface';
import { ILanguageService } from 'app/projects/shared/src/lib/interfaces/language.interface';
import { MAXBRAIN_LANGUAGE_SERVICE } from 'app/projects/shared/src/lib/services/language.token';
import { MAXBRAIN_ENVIRONMENT } from 'app/projects/core/src/lib/services/environment.token';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { CourseCatalogApiService } from 'app/library/course-catalog/services/course-catalog.api-service';
import { CourseCatalog } from 'app/library/course-catalog/models/course-catalog.model';
import { firstValueFrom } from 'rxjs';
import { IComponentCanDiscardChanges } from 'app/services/discard-changes.guard';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MaxBrainConfirmDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/confirm/confirm.component';
import { CourseOfferingSelectCategoriesDialogComponent } from 'app/library/course-offerings-category/components/dialogs/move-to-dialog/course-offering-select-catagories-dialog.component';
import { CourseStructureCategory } from 'app/library/course-offerings-category/models/course-structure-category.model';
import { CourseStructureCategoryService } from 'app/library/course-offerings-category/services/course-structure-category.service';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { CourseCatalogCategory } from 'app/library/course-catalog/models/course-catalog-category.model';
import { CourseCatalogPreviewDialogComponent } from 'app/library/course-catalog/dialogs/catalog-preview/course-catalog-preview.component';

@Component({
    selector: 'app-experience-catalog',
    templateUrl: './experience-catalog.component.html',
    styleUrls: ['./experience-catalog.component.scss'],
})
export class ExperienceCatalogComponent extends UnsubscribeOnDestroy implements OnInit, IComponentCanDiscardChanges {
    experienceId: string;
    experience: MaxBrainExperience;
    experienceTitleTranslations: { key: string };
    courseExperienceCatalog: CourseCatalog;
    discardChangesDialogRef: MatDialogRef<MaxBrainConfirmDialogComponent>;
    selectCategoriesRef: MatDialogRef<CourseOfferingSelectCategoriesDialogComponent>;
    courseCatalogPreviewDialogRef: MatDialogRef<CourseCatalogPreviewDialogComponent>;

    growKeywords: string[] = [];
    visible = new FormControl(false);
    selectedExperienceLanguageId: string;
    currencyOptions: string[] = [CurrencyCode.CHF, CurrencyCode.EUR, CurrencyCode.USD];
    defaultCurrency: CurrencyCode = CurrencyCode.CHF;
    private _isAutoSaving = false;
    private _hasPendingAutoSave = false;

    get isDataChanged(): boolean {
        return !this.formGroup || this.formGroup.dirty;
    }

    get title() {
        return this.formGroup.get('title');
    }

    private _formGroup: FormGroup;

    get formGroup(): FormGroup {
        return this._formGroup;
    }

    screenWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;

    @HostListener('window:resize')
    onResize() {
        this.screenWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    }

    constructor(
        @Inject(MAXBRAIN_ENVIRONMENT) private _environment: IMaxBrainEnvironment,
        @Inject(MAXBRAIN_LANGUAGE_SERVICE) public languageService: ILanguageService,
        private _experienceWidgetsService: ExperienceWidgetService,
        private _experienceService: ExperienceService,
        private _translateService: TranslateService,
        private _matSnackBar: MatSnackBar,
        private _location: Location,
        private _route: ActivatedRoute,
        private _formBuilder: FormBuilder,
        private _matDialog: MatDialog,
        private _filterLanguagesPipe: FilterLanguagesPipe,
        public sidenavService: SidenavService,
        private _courseCatalog: CourseCatalogApiService,
        private _courseStructureCategoryService: CourseStructureCategoryService,
        private _tenantDataService: TenantDataService
    ) {
        super();
    }

    async ngOnInit(): Promise<void> {
        const tenantCurrencyConfig = getTenantCurrencyConfig(this._tenantDataService.tenantConfig);
        this.currencyOptions = tenantCurrencyConfig.currencies;
        this.defaultCurrency = tenantCurrencyConfig.defaultCurrency;

        this._formGroup = this._formBuilder.group({
            language: this._formBuilder.array([]),
            currency: [this.defaultCurrency, Validators.required],
        });

        this.experienceId = this._route.snapshot.parent.params.id;
        this.experience = this._experienceService.getExperienceById(this.experienceId);
        if (!this.experience) {
            await this._experienceService.fetchExperiences();
            this.experience = this._experienceService.getExperienceById(this.experienceId);
        }

        this.courseExperienceCatalog = await this._courseCatalog.getExperienceCatalog(this.experienceId).toPromise();
        const selectedCurrency = this.experience?.currency || this.courseExperienceCatalog.currency || this.defaultCurrency;
        this.courseExperienceCatalog.currency = selectedCurrency;
        this.currencyOptions = Array.from(new Set([this.defaultCurrency, ...this.currencyOptions, selectedCurrency].filter((currency) => !!currency)));
        this.formGroup.get('currency')?.setValue(selectedCurrency, { emitEvent: false });
        const selectedLanguages = this._filterLanguagesPipe.transform(this.languageService.languages);
        this.setExperienceLanguagesForm(selectedLanguages[0].id);

        this.visible.setValue(this.courseExperienceCatalog.visibleToUsers);

        this._experienceService.saveExperienceChangesSubject$.pipe(takeUntil(this._unsubscribeAll)).subscribe((saveChanges) => {
            if (saveChanges) {
                this.saveChanges();
                this._experienceService.saveExperienceChanges(false);
            }
        });

        this.growKeywords = await this._experienceService.fetchGrowKeywords();

        this.formGroup
            .get('currency')
            .valueChanges.pipe(takeUntil(this._unsubscribeAll))
            .subscribe((currency: string) => {
                this.courseExperienceCatalog.currency = currency;
            });

        this.formGroup.valueChanges.pipe(debounceTime(800), takeUntil(this._unsubscribeAll)).subscribe(() => {
            this.autoSaveChanges();
        });
    }

    private async autoSaveChanges(): Promise<void> {
        if (!this.formGroup?.dirty) {
            return;
        }

        if (this._isAutoSaving) {
            this._hasPendingAutoSave = true;
            return;
        }

        this._isAutoSaving = true;
        try {
            await this.saveChanges(false);
        } finally {
            this._isAutoSaving = false;

            if (this._hasPendingAutoSave) {
                this._hasPendingAutoSave = false;
                await this.autoSaveChanges();
            }
        }
    }

    async setExperienceLanguagesForm(langId: string) {
        this.changeExperienceLanguage(langId);
        this.selectedExperienceLanguageId = 'translation_' + langId;

        const titleTranslations = this.courseExperienceCatalog.titleTranslations;

        const languageControl = this._formGroup.get('language') as FormArray;
        languageControl.clear();

        Object.keys(titleTranslations).forEach((key) => {
            languageControl.push(
                this._formBuilder.group({
                    id: [key, Validators.required],
                    title: [this.decodeHtmlEntities(titleTranslations[key]), Validators.required],
                })
            );
        });
    }

    decodeHtmlEntities(label: string): string {
        let txt = document.createElement('textarea');
        txt.innerHTML = label;
        return txt.value;
    }

    preview(): void {
        this.courseCatalogPreviewDialogRef = this._matDialog.open(CourseCatalogPreviewDialogComponent, {
            disableClose: false,
            width: '95vw',
            height: '95vh',
            maxWidth: '100vw',
            panelClass: 'full-dialog',
        });

        this.courseCatalogPreviewDialogRef.componentInstance.catalogId = this.courseExperienceCatalog.id;
    }

    addCategory(): void {}

    async visibleToggleChanged(event: MatSlideToggleChange): Promise<void> {
        const previousValue = !event.checked;
        const confirmDialogRef = this._matDialog.open(MaxBrainConfirmDialogComponent, {
            disableClose: false,
            width: '600px',
        });

        const messageKey = event.checked ? 'COURSE_CATALOG.DIALOG.CHANGE_VISIBILITY.MESSAGE.SHOW' : 'COURSE_CATALOG.DIALOG.CHANGE_VISIBILITY.MESSAGE.HIDE';

        confirmDialogRef.componentInstance.confirmTitle = 'COURSE_CATALOG.DIALOG.CHANGE_VISIBILITY.TITLE';
        confirmDialogRef.componentInstance.confirmMessage = this._translateService.instant(messageKey);
        confirmDialogRef.componentInstance.actionButtonText = 'GENERAL.BUTTON.CONFIRM';

        confirmDialogRef.afterClosed().subscribe(async (result) => {
            if (result) {
                await this.updateCatalogVisibility(event.checked);
            } else {
                this.visible.setValue(previousValue);
                event.source.checked = previousValue;
            }
        });
    }

    private async updateCatalogVisibility(visibleToUsers: boolean): Promise<void> {
        const previousValue = this.courseExperienceCatalog.visibleToUsers;
        this.courseExperienceCatalog.visibleToUsers = visibleToUsers;

        const updatedCatalog = await firstValueFrom(this._courseCatalog.updateExperienceCatalog(this.courseExperienceCatalog.id, this.courseExperienceCatalog));

        if (updatedCatalog) {
            this.courseExperienceCatalog = updatedCatalog;
        } else {
            this.courseExperienceCatalog.visibleToUsers = previousValue;
            this.visible.setValue(previousValue);
        }
    }

    copyKeywordToClipboard(keyword: string): void {
        const textToCopy = `{${keyword}}`;
        navigator.clipboard.writeText(textToCopy).then(() => {
            const successMessage = this._translateService.instant('GENERAL.MESSAGE.KEYWORD_COPIED');

            this._matSnackBar.open(successMessage, 'OK', {
                verticalPosition: 'top',
                duration: SnackBarTime.fast,
            });
        });
    }

    async changeExperienceLanguage(langId: string) {
        this.selectedExperienceLanguageId = 'translation_' + langId;
    }

    checkValue(langId: string) {
        const key = 'translation_' + langId;
        const translations = this.courseExperienceCatalog.titleTranslations;
        return translations[key] && translations[key].trim() !== '';
    }

    openAddCategoryDialog(): void {
        this.selectCategoriesRef = this._matDialog.open(CourseOfferingSelectCategoriesDialogComponent, {
            disableClose: false,
            width: '760px',
            autoFocus: false,
        });

        const categories = this._courseStructureCategoryService.getCourseStructureCategories();
        const clonedCategories: CourseStructureCategory[] = JSON.parse(JSON.stringify(categories));
        clonedCategories.forEach((category) => {
            category.isSelectable = false;
            category.isSelected = false;
        });

        const categoryListTree = this._courseStructureCategoryService.convertListToTree(clonedCategories, true);
        const alreadySelected = [...(this.courseExperienceCatalog.categories || [])].sort((a, b) => a.sequence - b.sequence);

        this.selectCategoriesRef.componentInstance.title = this._translateService.instant('COURSES.BUTTON.ADD_CATEGORY');
        this.selectCategoriesRef.componentInstance.listTree = categoryListTree;
        this.selectCategoriesRef.componentInstance.expandFirstItem = true;
        this.selectCategoriesRef.componentInstance.selectedCategoriesList = alreadySelected;

        this.selectCategoriesRef.afterClosed().subscribe((selectedCategories: CourseStructureCategory[]) => {
            if (selectedCategories) {
                const selectedIds = selectedCategories.map((c) => Number(c.id));

                this._courseCatalog.updateCatalogCategories(this.courseExperienceCatalog.id, selectedIds).subscribe({
                    next: (updatedCatalog) => {
                        void this.applyCatalogCategoriesInSelectedOrder(updatedCatalog, selectedIds);
                    },
                });
            }

            this.selectCategoriesRef = null;
        });
    }

    private async applyCatalogCategoriesInSelectedOrder(catalog: CourseCatalog, selectedIds: number[]): Promise<void> {
        const currentOrder = (catalog.categories || []).map((category) => Number(category.id));
        const orderMatches = selectedIds.length === currentOrder.length && selectedIds.every((id, index) => id === currentOrder[index]);

        if (!orderMatches) {
            for (let index = 0; index < selectedIds.length; index++) {
                const category = catalog.categories.find((item) => Number(item.id) === selectedIds[index]);
                if (category && category.sequence !== index) {
                    await this._courseCatalog.updateCatalogCategoryOrder(catalog.id, category.id, { sequence: index });
                }
            }
            this.courseExperienceCatalog = await firstValueFrom(this._courseCatalog.getExperienceCatalog(this.experienceId));
            return;
        }

        this.courseExperienceCatalog = catalog;
    }

    openRemoveCourseStructureCategoryModal(category: CourseStructureCategory): void {
        const confirmDialogRef = this._matDialog.open(MaxBrainConfirmDialogComponent, {
            disableClose: false,
            width: '480px',
        });

        confirmDialogRef.componentInstance.confirmTitle = 'GENERAL.BUTTON.CONFIRM';
        confirmDialogRef.componentInstance.confirmButtonColor = 'warn';
        confirmDialogRef.componentInstance.confirmMessage = this._translateService.instant('COURSE_CATALOG.MESSAGE.REMOVE_CATEGORY_CONFIRMATION', { category: category.name });
        confirmDialogRef.componentInstance.actionButtonText = 'GENERAL.BUTTON.REMOVE';

        confirmDialogRef.afterClosed().subscribe(async (confirmed) => {
            if (confirmed) {
                const remainingIds = this.courseExperienceCatalog.categories.filter((c) => c.id !== category.id).map((c) => Number(c.id));
                const updatedCatalog = await firstValueFrom(this._courseCatalog.updateCatalogCategories(this.courseExperienceCatalog.id, remainingIds));

                if (updatedCatalog) {
                    this.courseExperienceCatalog = updatedCatalog;
                }
            }
        });
    }

    async saveChanges(showSuccessMessage = true) {
        const languageFormControl = this.formGroup.get('language') as FormArray;

        const updatedTranslations = {} as typeof this.courseExperienceCatalog.titleTranslations;

        languageFormControl.controls.forEach((ctrl) => {
            const key = ctrl.get('id')?.value;
            const value = ctrl.get('title')?.value;
            updatedTranslations[key] = value;
        });

        const hasTitleChanges = JSON.stringify(this.courseExperienceCatalog.titleTranslations) !== JSON.stringify(updatedTranslations);
        const selectedCurrency = this.formGroup.get('currency')?.value || this.defaultCurrency;
        const hasCurrencyChanges = this.experience?.currency !== selectedCurrency;
        let isSuccessful = true;

        if (hasTitleChanges) {
            this.courseExperienceCatalog.titleTranslations = updatedTranslations;
            const updatedCatalog = await firstValueFrom(this._courseCatalog.updateExperienceCatalog(this.courseExperienceCatalog.id, this.courseExperienceCatalog));
            isSuccessful = !!updatedCatalog;

            if (updatedCatalog) {
                this.courseExperienceCatalog = updatedCatalog;
            }
        }

        if (hasCurrencyChanges && this.experience) {
            this.experience.currency = selectedCurrency;
            const experienceUpdated = await this._experienceService.updateExperience(this.experience);
            isSuccessful = isSuccessful && !!experienceUpdated;

            if (experienceUpdated) {
                this.courseExperienceCatalog.currency = selectedCurrency;
            }
        }

        if (isSuccessful && (hasTitleChanges || hasCurrencyChanges)) {
            this.formGroup.markAsPristine();
            if (showSuccessMessage) {
                const successMessage = this._translateService.instant('ENGAGE.MESSAGE.CHANGES_SAVED');
                this._matSnackBar.open(successMessage, 'OK', {
                    verticalPosition: 'top',
                    duration: SnackBarTime.medium,
                });
            }
        }
    }

    async moveCategory(category: CourseCatalogCategory, newIndex: number): Promise<void> {
        try {
            await this._courseCatalog.updateCatalogCategoryOrder(this.courseExperienceCatalog.id, category.id, { sequence: newIndex });
            this.courseExperienceCatalog = await this._courseCatalog.getExperienceCatalog(this.experienceId).toPromise();
        } catch (err) {
            console.error('Failed to reorder category', err);
        }
    }

    async moveCourseStructureCategoryUp(category: CourseCatalogCategory) {
        const currentIndex = this.courseExperienceCatalog.categories.findIndex((c) => c.id === category.id);
        if (currentIndex <= 0) return;
        await this.moveCategory(category, currentIndex - 1);
    }

    async moveCourseStructureCategoryDown(category: CourseCatalogCategory) {
        const currentIndex = this.courseExperienceCatalog.categories.findIndex((c) => c.id === category.id);
        if (currentIndex < 0 || currentIndex === this.courseExperienceCatalog.categories.length - 1) return;
        await this.moveCategory(category, currentIndex + 1);
    }

    async dropSubcategory(event: CdkDragDrop<CourseCatalogCategory[]>) {
        if (event.previousIndex === event.currentIndex) return;
        const movedCategory = this.courseExperienceCatalog.categories[event.previousIndex];
        await this.moveCategory(movedCategory, event.currentIndex);
    }

    cancelChanges(): void {
        if (this.isDataChanged) {
            this.discardChangesDialogRef = this._matDialog.open(MaxBrainConfirmDialogComponent, {
                disableClose: false,
            });

            this.discardChangesDialogRef.componentInstance.confirmTitle = 'GENERAL.BUTTON.CONFIRM';
            this.discardChangesDialogRef.componentInstance.confirmMessage = 'GENERAL.MESSAGE.DISCARD_CHANGES';

            this.discardChangesDialogRef.afterClosed().subscribe((result) => {
                if (result) {
                    //this.moduleFormService.createFormGroup();
                }
                this.discardChangesDialogRef = null;
            });
        }
    }

    goBack() {
        this._location.back();
    }

    openEditExperienceWidgetDialog(): void {}

    ngOnDestroy(): void {}
}
